import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, TrendingUp, Activity, Settings as SettingsIcon,
  BarChart3, Users, Crown, ShieldCheck, UserCog, LogOut, Menu, X, TestTube, ShoppingCart, BookOpen, Zap, Briefcase, ListChecks, ArrowUpRight,
  Fuel, LineChart, Radio, Wallet, PieChart, SlidersHorizontal, Copy, Rss, FlaskConical,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { Logo } from "../components/Logo";

const NavItem = ({ to, icon: Icon, label, testid, active }) => (
  <Link
    to={to}
    data-testid={testid}
    className="flex items-center gap-3 px-3 py-2 text-xs uppercase tracking-[0.15em] font-mono-data hover:bg-white/[0.04]"
    style={{
      color: active ? "var(--bull)" : "var(--text-muted)",
      borderLeft: active ? "2px solid var(--bull)" : "2px solid transparent",
    }}
  >
    <Icon size={14} /> {label}
  </Link>
);

export const AppShell = ({ children }) => {
  const { user, isAdmin, isPowerUser, hasAutoTradingAccess, hasTipsAccess, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const path = location.pathname;
  const navigate = useNavigate();

  if (!user) return children;

  return (
    // 2026-09-12 — "Terminal" theme rollout (chat: "Seems to be okay...Roll
    // out further" after piloting on just the Auto Picks tab, see
    // Dashboard.jsx / index.css's .theme-terminal comments for the original
    // pilot). Applying the class once here, at the shell root, cascades the
    // warm-palette CSS variables to the sidebar + every routed page under
    // Shell (Dashboard's every tab, Commodities, Journal, Admin, etc.) with
    // no per-page edits needed — same reasoning as the pilot: everything
    // already colors itself via var(--surface)/var(--bull)/var(--bear)/etc.
    // Known gap: Radix-portaled UI (Dialog/DropdownMenu/Select/Toast) mounts
    // to document.body by default, outside this div, so those may still show
    // the old palette until each is given an explicit container — call this
    // out if a modal/toast looks inconsistent. Public Landing page and the
    // pre-Shell /profile-setup, /pending onboarding routes are NOT under
    // AppShell and intentionally untouched by this rollout.
    <div className="theme-terminal min-h-screen flex" style={{ background: "var(--bg-primary)" }}>
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 border-r border-grid bg-black/40 backdrop-blur transition-transform md:translate-x-0 md:static flex flex-col
          ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className="px-4 h-14 border-b border-grid flex items-center justify-between shrink-0">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("/dashboard")}
          >
            <Logo size={20} />
            <span className="font-heading text-lg tracking-tight">StoxTrade</span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="md:hidden"
            data-testid="sidebar-close"
          ><X size={16} /></button>
        </div>

        <nav className="py-3 space-y-0.5 flex-1 min-h-0 overflow-y-auto">
          <NavItem to="/dashboard" icon={Home} label="Home" testid="nav-home" active={path === "/dashboard"} />
          {hasTipsAccess && (
            <NavItem to="/dashboard?tab=tips" icon={TrendingUp} label="Stock Tips" testid="nav-tips" active={path === "/dashboard" && location.search.includes("tab=tips")} />
          )}
          {hasAutoTradingAccess && (
            <NavItem to="/dashboard?tab=autopicks" icon={Activity} label="Auto Picks" testid="nav-autopicks" active={location.search.includes("tab=autopicks")} />
          )}
          {/* 2026-08-28 — pulled out of the Auto Picks tab into its own
              top-level page. 2026-10-13 — chat: "For new user, they don't see
              Live Orders menu at all. At least user should see Live orders
              and list of trades" — now shown to every signed-in user; the
              page itself shows a non-admin only their own trades. */}
          <NavItem to="/live-orders" icon={Rss} label="Live Orders" testid="nav-live-orders" active={path === "/live-orders"} />
          {/* 2026-10-26 — Manual Order for every user (their own Kite accounts) */}
          <NavItem to="/manual-order" icon={ShoppingCart} label="Manual Order" testid="nav-manual-order" active={path === "/manual-order"} />
          <NavItem to="/settings" icon={SettingsIcon} label="Settings" testid="nav-settings" active={path === "/settings"} />
          <NavItem to="/reports" icon={BarChart3} label="P&L Reports" testid="nav-reports" active={path === "/reports"} />
          {/* 2026-09-13 — chat: "Backtests, Journal, Momentum scanner,
              Steady, portfolio should be available only for Admin (as most
              of them are still in development)." Matches the requireRole
              gate added to each of these routes in App.js. */}
          {isAdmin && (
            <>
              <NavItem to="/backtests" icon={TestTube} label="Backtests" testid="nav-backtests" active={path === "/backtests"} />
              <NavItem to="/journal" icon={BookOpen} label="Journal" testid="nav-journal" active={path === "/journal"} />
              <NavItem to="/setup-g" icon={FlaskConical} label="Setup G · NIFTY (paper)" testid="nav-setup-g" active={path === "/setup-g"} />
              <NavItem to="/momentum" icon={Zap} label="Momentum Scanner" testid="nav-momentum" active={path === "/momentum"} />
              <NavItem to="/steady-intraday-trend" icon={ArrowUpRight} label="Steady Intraday Trend" testid="nav-steady-trend" active={path === "/steady-intraday-trend" || path === "/linear-accumulation"} />
              <NavItem to="/portfolio" icon={Briefcase} label="Portfolio" testid="nav-portfolio" active={path === "/portfolio"} />
            </>
          )}

          {/* 2026-08-21 — Commodities (MCX). New, dedicated top-level menu —
              own screens throughout, deliberately not nested under any
              Stocks-related page (chat: "don't re-use Stocks related
              screens, commodities need to have new screens like
              configuration etc under new menu Commodities"). */}
          <div className="pt-4 mt-4 border-t border-grid">
            <div className="px-3 text-[9px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
              Commodities
            </div>
            <NavItem to="/commodities" icon={Fuel} label="Overview" testid="nav-commodities-overview" active={path === "/commodities"} />
            <NavItem to="/commodities/strategies" icon={LineChart} label="Strategies" testid="nav-commodities-strategies" active={path === "/commodities/strategies"} />
            <NavItem to="/commodities/signals" icon={Radio} label="Signals" testid="nav-commodities-signals" active={path === "/commodities/signals"} />
            {/* 2026-09-13 — chat: "Execution (make it Live Orders) and
                should be user specific" — moved out of the admin-only block
                below; the page itself now shows a user-scoped view to
                everyone and the admin control panel only to ADMIN viewers. */}
            <NavItem to="/commodities/execution" icon={Rss} label="Live Orders" testid="nav-commodities-live-orders" active={path === "/commodities/execution"} />
            <NavItem to="/commodities/pnl" icon={PieChart} label="P&L" testid="nav-commodities-pnl" active={path === "/commodities/pnl"} />
            {isAdmin && (
              <>
                {/* 2026-09-13 — chat: "Commodities: Paper Trading for Admin
                    only." — moved here from the always-visible block above. */}
                <NavItem to="/commodities/paper-trading" icon={Wallet} label="Paper Trading" testid="nav-commodities-paper-trading" active={path === "/commodities/paper-trading"} />
                {/* 2026-10-08 — Commodities Journal (paper-trade analytics, admin only) */}
                <NavItem to="/commodities/journal" icon={BookOpen} label="Journal" testid="nav-commodities-journal" active={path === "/commodities/journal"} />
                <NavItem to="/commodities/configuration" icon={SlidersHorizontal} label="Configuration" testid="nav-commodities-configuration" active={path === "/commodities/configuration"} />
              </>
            )}
          </div>

          {isAdmin && (
            <div className="pt-4 mt-4 border-t border-grid">
              <div className="px-3 text-[9px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
                Admin
              </div>
              <NavItem to="/admin/users" icon={Users} label="Users" testid="nav-admin-users" active={path === "/admin/users"} />
              <NavItem to="/admin/subscriptions" icon={Crown} label="Subscriptions" testid="nav-admin-subs" active={path === "/admin/subscriptions"} />
              <NavItem to="/admin/configuration" icon={UserCog} label="Configuration" testid="nav-admin-config" active={path === "/admin/configuration"} />
              <NavItem to="/admin/alerts" icon={ShieldCheck} label="Alerts" testid="nav-admin-alerts" active={path === "/admin/alerts"} />
              <NavItem to="/admin/jobs" icon={ListChecks} label="Jobs & Scans" testid="nav-admin-jobs" active={path === "/admin/jobs"} />
              <NavItem to="/admin/paper-fanout" icon={Copy} label="Paper Fan-Out" testid="nav-admin-paper-fanout" active={path === "/admin/paper-fanout"} />
            </div>
          )}
        </nav>

        {/* User panel */}
        <div className="border-t border-grid p-3 space-y-2 shrink-0">
          <div className="text-[10px] font-mono-data uppercase tracking-wider">
            <div className="text-muted-1">{user.profile?.display_name || user.name}</div>
            <div className="text-muted-3">{user.email}</div>
          </div>
          <div className="flex items-center gap-2 text-[9px] font-mono-data uppercase tracking-wider">
            <span style={{ color: isAdmin ? "var(--bull)" : isPowerUser ? "var(--bull)" : "var(--muted-3)" }}>
              {user.role}
            </span>
            <span className="text-muted-3">·</span>
            <span style={{ color: hasAutoTradingAccess ? "var(--bull)" : "var(--muted-3)" }}>
              {user.subscription}
            </span>
          </div>
          <button
            onClick={logout}
            data-testid="nav-logout"
            className="w-full px-3 py-1.5 border border-grid text-[10px] uppercase tracking-wider font-mono-data flex items-center justify-center gap-2 hover:bg-white/[0.04]"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="h-14 border-b border-grid flex items-center justify-between px-4 md:px-6 md:hidden">
          <button onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu size={18} />
          </button>
          <div className="font-heading">StoxTrade</div>
          <div />
        </header>
        <main className="px-4 md:px-8 py-6 md:py-10 max-w-[1600px] mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
